import { cn } from '@flowstate/utils';

function Layer({
  label,
  z,
  children,
  className,
}: {
  label: string;
  z: number;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "absolute inset-0 rounded-3xl border border-primary/30 bg-white/[0.06] backdrop-blur-sm shadow-2xl",
        className
      )}
      style={{
        transform: `translateZ(${z}px)`,
        transformStyle: "preserve-3d",
        boxShadow:
          "0 30px 100px -30px rgba(0,0,0,0.7), inset 0 0 0 1px rgba(255,255,255,0.08)",
      }}
    >
      <div className="absolute inset-0 overflow-hidden rounded-3xl">
        <div className="absolute inset-0 bg-gradient-to-tr from-primary/10 via-transparent to-transparent" />
        {children}
        <div className="absolute bottom-5 left-6">
          <span className="text-xs font-bold tracking-[0.25em] text-primary/90 uppercase">
            {label}
          </span>
        </div>
      </div>
    </div>
  );
}

function AgentsLayer() {
  return (
    <svg className="absolute inset-0 h-full w-full" viewBox="0 0 700 460" fill="none">
      <defs>
        <filter id="agentsGlow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="4" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <g stroke="var(--primary)" strokeOpacity="0.35" strokeWidth="1.5">
        <line x1="180" y1="120" x2="350" y2="200" />
        <line x1="520" y1="120" x2="350" y2="200" />
        <line x1="220" y1="320" x2="350" y2="200" />
        <line x1="480" y1="320" x2="350" y2="200" />
        <line x1="180" y1="120" x2="520" y2="120" />
        <line x1="220" y1="320" x2="480" y2="320" />
        <line x1="180" y1="120" x2="480" y2="320" />
        <line x1="520" y1="120" x2="220" y2="320" />
      </g>
      <g filter="url(#agentsGlow)">
        <circle cx="350" cy="200" r="12" className="fill-primary" />
        <circle cx="180" cy="120" r="7" className="fill-primary/80" />
        <circle cx="520" cy="120" r="7" className="fill-primary/80" />
        <circle cx="220" cy="320" r="7" className="fill-primary/80" />
        <circle cx="480" cy="320" r="7" className="fill-primary/80" />
      </g>
      <circle cx="180" cy="120" r="4" className="fill-primary">
        <animate attributeName="cx" values="180;350;520" dur="4s" repeatCount="indefinite" />
        <animate attributeName="cy" values="120;200;120" dur="4s" repeatCount="indefinite" />
      </circle>
      <circle cx="220" cy="320" r="4" className="fill-primary">
        <animate attributeName="cx" values="220;350;480" dur="5s" repeatCount="indefinite" />
        <animate attributeName="cy" values="320;200;320" dur="5s" repeatCount="indefinite" />
      </circle>
    </svg>
  );
}

function StreamsLayer() {
  return (
    <svg className="absolute inset-0 h-full w-full" viewBox="0 0 700 460" fill="none">
      <defs>
        <filter id="streamsGlow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="3" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <g stroke="var(--primary)" strokeOpacity="0.25" strokeWidth="1" strokeDasharray="4 6">
        <line x1="60" y1="140" x2="640" y2="140" />
        <line x1="60" y1="230" x2="640" y2="230" />
        <line x1="60" y1="320" x2="640" y2="320" />
      </g>
      <g fill="var(--primary)" fillOpacity="0.6">
        <circle cx="60" cy="140" r="5" />
        <circle cx="60" cy="230" r="5" />
        <circle cx="60" cy="320" r="5" />
      </g>
      <g filter="url(#streamsGlow)">
        <circle cx="60" cy="140" r="6" className="fill-primary">
          <animate attributeName="cx" values="60;640" dur="3.5s" repeatCount="indefinite" />
        </circle>
        <circle cx="60" cy="230" r="6" className="fill-primary">
          <animate attributeName="cx" values="60;640" dur="4.2s" repeatCount="indefinite" />
        </circle>
        <circle cx="60" cy="320" r="6" className="fill-primary">
          <animate attributeName="cx" values="60;640" dur="5s" repeatCount="indefinite" />
        </circle>
      </g>
    </svg>
  );
}

function SyncLayer() {
  return (
    <svg className="absolute inset-0 h-full w-full" viewBox="0 0 700 460" fill="none">
      <defs>
        <pattern id="syncGrid" width="70" height="70" patternUnits="userSpaceOnUse">
          <circle cx="35" cy="35" r="1.5" className="fill-primary/50" />
        </pattern>
        <filter id="syncGlow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="4" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <rect x="0" y="0" width="700" height="460" fill="url(#syncGrid)" />
      <g filter="url(#syncGlow)">
        <circle cx="175" cy="140" r="7" className="fill-primary" />
        <circle cx="525" cy="140" r="7" className="fill-primary" />
        <circle cx="350" cy="280" r="10" className="fill-primary">
          <animate attributeName="r" values="10;12;10" dur="2s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="1;0.6;1" dur="2s" repeatCount="indefinite" />
        </circle>
        <circle cx="175" cy="350" r="7" className="fill-primary" />
        <circle cx="525" cy="350" r="7" className="fill-primary" />
      </g>
      <g stroke="var(--primary)" strokeOpacity="0.3" strokeWidth="1.5">
        <line x1="175" y1="140" x2="350" y2="280" />
        <line x1="525" y1="140" x2="350" y2="280" />
        <line x1="175" y1="350" x2="350" y2="280" />
        <line x1="525" y1="350" x2="350" y2="280" />
      </g>
    </svg>
  );
}

export function MyceliumLayeredScene({ className }: { className?: string }) {
  return (
    <div className={cn("relative w-full h-[min(70vw,620px)]", className)}>
      <div
        className="absolute inset-0 flex items-center justify-center"
        style={{ perspective: "1400px", transformStyle: "preserve-3d" }}
      >
        <div
          className="relative w-[min(80vw,760px)] h-[min(50vw,480px)]"
          style={{
            transform: "rotateX(60deg) rotateZ(-35deg)",
            transformStyle: "preserve-3d",
          }}
        >
          <Layer label="SYNC" z={-100} className="opacity-60">
            <SyncLayer />
          </Layer>
          <Layer label="STREAMS" z={-50} className="opacity-80">
            <StreamsLayer />
          </Layer>
          <Layer label="AGENTS" z={0}>
            <AgentsLayer />
          </Layer>
        </div>
      </div>
    </div>
  );
}
